import React, { forwardRef, useImperativeHandle } from "react";
import { observer, useLocalObservable } from "mobx-react";
import { Modal, Button } from "antd";
import AceEditor from "react-ace";

import "ace-builds/src-min-noconflict/ext-searchbox";
import "ace-builds/src-min-noconflict/mode-json";
import "ace-builds/src-min-noconflict/theme-tomorrow";
import dayjs from "dayjs";

import { translateKey as t } from "../../locales/translation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleLeft, faDownload } from "@fortawesome/free-solid-svg-icons";

interface JSONViewerProps {
  ref: any;
  download?: boolean;
}

const JSONViewer: React.FC<JSONViewerProps> = forwardRef((download, ref) => {
  const store = useLocalObservable(() => ({
    modalVisible: false,
    name: "",
    jsonObject: null,
    jsonFilename: null,
    download: true, // default prop

    // Actions to update observables
    setModalVisible(value) {
      this.modalVisible = value;
    },
    setName(value) {
      this.name = value;
    },
    setJsonObject(value) {
      this.jsonObject = value;
    },
    setJsonFilename(value) {
      this.jsonFilename = value;
    },
    toggleDownload() {
      this.download = !this.download;
    },
  }));

  useImperativeHandle(ref, () => ({
    loadModal(name, jsonObject, jsonFilename = null) {
      store.setName(name);
      store.setJsonObject(jsonObject);
      store.setJsonFilename(jsonFilename);
      setModalState(true);
    },
  }));

  const setModalState = (state) => {
    store.setModalVisible(state);

    if (state === false) {
      // Free up the object.
      store.setJsonObject([]);
      // Reset the file name.
      store.setJsonFilename(null);
    }
  };

  const downloadJson = () => {
    // Get the file name.
    let filename = store.jsonFilename ? store.jsonFilename : "JSON_Export";
    // Append unique timestamp.
    filename = filename + "_" + dayjs().format();
    let contentType = "application/json;charset=utf-8;";
    const nav: any = window.navigator;

    if (nav && nav.msSaveOrOpenBlob) {
      let blob = new Blob([decodeURIComponent(encodeURI(JSON.stringify(store.jsonObject)))], { type: contentType });
      nav.msSaveOrOpenBlob(blob, filename);
    } else {
      let a = document.createElement("a");
      a.download = filename;
      a.href = "data:" + contentType + "," + encodeURIComponent(JSON.stringify(store.jsonObject));
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  const renderJSONEditor = () => {
    if (!store.jsonObject) {
      return null;
    }

    return (
      <AceEditor
        mode="json"
        width="100%"
        height="70vh"
        fontSize={14}
        theme="tomorrow"
        name="jsonEditor"
        showPrintMargin={false}
        readOnly={true}
        wrapEnabled={true}
        value={JSON.stringify(store.jsonObject, null, 2)}
        editorProps={{ $blockScrolling: Infinity }}
        setOptions={{
          useWorker: false, 
        }}
      />
    );
  };

  const renderFooter = () => {
    let footer = [
      <Button
        key="return"
        type="primary"
        icon={<FontAwesomeIcon icon={faAngleLeft} />}
        onClick={() => {
          setModalState(false);
        }}
      >
        {t("lblReturn", "Return")}
      </Button>,
    ];

    if (download) {
      footer.push(
        <Button key="download" onClick={() => downloadJson()} icon={<FontAwesomeIcon icon={faDownload} />}>
          {t("lblDownloadJson", "Download JSON")}
        </Button>
      );
    }

    return footer;
  };

  return (
    <Modal
      styles={{ body: { zIndex: 100, padding: 0 } }}
      destroyOnClose={true}
      title={store.name}
      width="90%"
      open={store.modalVisible}
      centered={true}
      footer={renderFooter()}
      onCancel={() => setModalState(false)}
    >
      {renderJSONEditor()}
    </Modal>
  );
});

export default observer(JSONViewer);
